/* eslint-disable jsx-a11y/label-has-associated-control */
import { Stack } from "@altimateai/lego";
import { Button, Input, NativeSelect, Textarea } from "@altimateai/lego";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { DeleteIcon } from "@assets/icons";
import { InputParameter, InputParameterSchema } from "./constants";
import { useEffect } from "react";
// Input parameter types that users can choose from
const INPUT_PARAMETER_TYPES = [
  { label: "String", value: "string" },
  { label: "Number", value: "number" },
  { label: "Boolean", value: "boolean" },
  { label: "Object", value: "object" },
] as const;

type InputType = (typeof INPUT_PARAMETER_TYPES)[number]["value"];

interface InputParameterFormProps {
  initialValue?: InputParameter;
  onSave: (input: InputParameter) => void;
  onCancel: () => void;
}

const InputParameterForm = ({ initialValue, onSave, onCancel }: InputParameterFormProps) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<InputParameter>({
    resolver: zodResolver(InputParameterSchema),
    defaultValues: initialValue || {
      name: "",
      type: "string",
      description: "",
      required: false,
    },
  });

  const nameValue = watch("name");

  useEffect(() => {
    if (nameValue) {
      setValue("key", nameValue.replace(/ /g, "-"));
    }
  }, [nameValue, setValue]);

  const onSubmit = (data: InputParameter) => {
    onSave(data);
  };

  const isEditing = !!initialValue?.key;

  return (
    <Stack direction="column" className="al-gap-2 al-p-4 al-border al-rounded-md">
      <Stack justify="between" align="center">
        <h4 className="al-text-sm al-font-medium">
          {isEditing ? "Edit Input Parameter" : "New Input Parameter"}
        </h4>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          <DeleteIcon className="al-w-4 al-h-4 al-text-destructive" />
        </Button>
      </Stack>
      <div>
        <Stack direction="column" className="al-gap-2">
          <Stack direction="column" className="al-gap-1">
            <label className="al-text-sm" htmlFor="input-name">
              Name
              <Input {...register("name")} placeholder="Parameter name" id="input-name" />
            </label>
            {errors.name && <p className="al-text-sm al-text-destructive">{errors.name.message}</p>}
          </Stack>
          <Stack direction="column" className="al-gap-1">
            <label className="al-text-sm" htmlFor="input-type">
              Type
              <NativeSelect
                size="sm"
                variant="ghost"
                options={[...INPUT_PARAMETER_TYPES]}
                value={watch("type")}
                onChange={(value: string) => setValue("type", value as InputType)}
                id="input-type"
              />
            </label>
          </Stack>
          <Stack direction="column" className="al-gap-1">
            <label className="al-text-sm" htmlFor="input-description">
              Description
              <Textarea
                {...register("description")}
                placeholder="Parameter description (optional)"
                className="al-h-[60px]"
                id="input-description"
              />
            </label>
          </Stack>
          <Stack direction="row" className="al-items-center al-gap-2">
            <label className="al-items-center al-gap-2 al-text-sm" htmlFor="input-required">
              <input
                type="checkbox"
                {...register("required")}
                className="al-h-4 al-w-4"
                id="input-required"
              />
              Required
            </label>
          </Stack>
          <Stack direction="row" className="al-gap-2 al-mt-2">
            <Button type="button" variant="outline" className="al-flex-1" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="button" className="al-flex-1" onClick={handleSubmit(onSubmit)}>
              {isEditing ? "Update Input" : "Save Input"}
            </Button>
          </Stack>
        </Stack>
      </div>
    </Stack>
  );
};

export { INPUT_PARAMETER_TYPES };
export type { InputType };
export default InputParameterForm;
